import { supabase } from '@/integrations/supabase/client';

export class SupabaseStorageService {
  private static readonly INTERVIEW_BUCKET = 'photos';
  private static readonly PROFILE_BUCKET = 'profile-images';
  
  /**
   * Upload an image to Supabase storage
   */
  static async uploadImage(file: File, folder = 'interview-photos'): Promise<string> {
    try {
      console.log(`[SupabaseStorage] Starting upload for file:`, {
        name: file.name,
        size: file.size,
        type: file.type,
        folder: folder
      });
      
      // No authentication required - this is for public interview submissions
      console.log(`[SupabaseStorage] Public upload - no authentication required`);
      
      // Use photoalbum bucket for all uploads (this is the correct public bucket)
      const bucket = 'photoalbum';
      
      // Create unique filename (no folder structure needed)
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      // Determine correct MIME type based on file extension as fallback
      const getMimeType = (extension: string) => {
        const mimeTypes: { [key: string]: string } = {
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg',
          'png': 'image/png',
          'gif': 'image/gif',
          'webp': 'image/webp'
        };
        return mimeTypes[extension] || 'image/jpeg';
      };
      
      const contentType = file.type && file.type.startsWith('image/') 
        ? file.type 
        : getMimeType(fileExt || 'jpg');
      
      console.log(`[SupabaseStorage] Using content type: ${contentType}`);
      console.log(`[SupabaseStorage] Uploading to bucket: ${bucket}, path: ${fileName}`);
      
      // Upload file to Supabase storage
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
          contentType: contentType
        });

      if (error) {
        console.error('[SupabaseStorage] Upload error:', error);
        throw new Error(`Upload failed: ${error.message}`);
      }

      console.log('[SupabaseStorage] Upload data:', data);

    // Get public URL
    const { data: publicUrlData } = supabase.storage
      .from(bucket)
      .getPublicUrl(fileName);

    // Ensure the URL has the correct "/object" segment
    const correctedUrl = publicUrlData.publicUrl.includes('/object/') 
      ? publicUrlData.publicUrl 
      : publicUrlData.publicUrl.replace('/storage/v1/public/', '/storage/v1/object/public/');
    console.log('[SupabaseStorage] Upload successful:', correctedUrl);
    return correctedUrl;
      
    } catch (error) {
      console.error('[SupabaseStorage] Upload error:', error);
      throw error;
    }
  }

  /**
   * Fetch uploaded images from a folder
   */
  static async fetchUploadedImages(folder = 'interview-photos'): Promise<string[]> {
    try {
      const bucket = folder.includes('profile') ? this.PROFILE_BUCKET : this.INTERVIEW_BUCKET;
      
      console.log(`[SupabaseStorage] Fetching images from bucket: ${bucket}, folder: ${folder}`);
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .list(folder, {
          limit: 100,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) {
        console.error('[SupabaseStorage] List error:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('[SupabaseStorage] No images found in folder');
        return [];
      }

      // Convert to public URLs
      const imageUrls = data
        .filter(file => this.isImageFile(file.name))
        .map(file => {
          const { data: publicUrlData } = supabase.storage
            .from(bucket)
            .getPublicUrl(`${folder}/${file.name}`);
          // Ensure the URL has the correct "/object" segment
          const url = publicUrlData.publicUrl.includes('/object/') 
            ? publicUrlData.publicUrl 
            : publicUrlData.publicUrl.replace('/storage/v1/public/', '/storage/v1/object/public/');
          return url;
        });

      console.log(`[SupabaseStorage] Found ${imageUrls.length} images`);
      return imageUrls;
      
    } catch (error) {
      console.error('[SupabaseStorage] Fetch error:', error);
      return [];
    }
  }

  /**
   * Delete an image from Supabase storage
   */
  static async deleteImage(url: string): Promise<boolean> {
    try {
      // Don't attempt to delete non-Supabase URLs
      if (!url.includes('supabase.co/storage')) {
        console.warn('[SupabaseStorage] Attempted to delete non-Supabase URL:', url);
        return false;
      }
      
      // Don't delete certain internal URLs
      if (url.includes('/assets/') || url.includes('placeholder')) {
        console.warn('[SupabaseStorage] Attempted to delete protected URL:', url);
        return false;
      }

      // Extract bucket and file path from URL
      // Handle both URL formats for backwards compatibility
      let urlParts = url.split('/storage/v1/object/public/');
      if (urlParts.length !== 2) {
        // Fallback to old format
        urlParts = url.split('/storage/v1/public/');
      }
      if (urlParts.length !== 2) {
        console.error('[SupabaseStorage] Invalid URL format:', url);
        return false;
      }

      const pathParts = urlParts[1].split('/');
      const bucket = pathParts[0];
      const filePath = pathParts.slice(1).join('/');

      console.log(`[SupabaseStorage] Deleting from bucket: ${bucket}, path: ${filePath}`);

      const { error } = await supabase.storage
        .from(bucket)
        .remove([filePath]);

      if (error) {
        console.error('[SupabaseStorage] Delete error:', error);
        return false;
      }

      console.log('[SupabaseStorage] Delete successful');
      return true;
      
    } catch (error) {
      console.error('[SupabaseStorage] Delete error:', error);
      return false;
    }
  }

  /**
   * Check Supabase storage connection
   */
  static async checkConnection(): Promise<{ connected: boolean; message: string }> {
    try {
      // Test connection by listing buckets
      const { data, error } = await supabase.storage.listBuckets();
      
      if (error) {
        console.error('[SupabaseStorage] Connection test failed:', error);
        return { 
          connected: false, 
          message: `Connection failed: ${error.message}` 
        };
      }

      const bucketNames = data?.map(b => b.name) || [];
      console.log('[SupabaseStorage] Available buckets:', bucketNames);
      
      return { 
        connected: true, 
        message: `Connected to Supabase Storage. Available buckets: ${bucketNames.join(', ')}` 
      };
      
    } catch (error) {
      console.error('[SupabaseStorage] Connection test error:', error);
      return { 
        connected: false, 
        message: `Connection error: ${error instanceof Error ? error.message : 'Unknown error'}` 
      };
    }
  }

  /**
   * Upload brand logos to storage
   */
  static async uploadBrandLogos() {
    try {
      console.log('[SupabaseStorage] Brand logos are now managed through static assets');
      
      // Return static asset URLs since logos should be consistent
      return {
        briefrLogoUrl: '/assets/briefr-logo.png',
        redaktorLogoUrl: '/assets/redaktor-logo.jpg'
      };
    } catch (error) {
      console.error('[SupabaseStorage] Brand logo error:', error);
      throw error;
    }
  }

  /**
   * Get logo URLs
   */
  static getLogoUrls() {
    return {
      briefrLogoUrl: '/assets/briefr-logo.png',
      redaktorLogoUrl: '/assets/redaktor-logo.jpg',
      newBriefrLogoUrl: '/assets/briefr-logo.png'
    };
  }

  /**
   * Validate image file type
   */
  static isValidImageFile(file: File): boolean {
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    return validTypes.includes(file.type);
  }

  /**
   * Validate file size
   */
  static isValidFileSize(file: File, maxSizeMB = 5): boolean {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    return file.size <= maxSizeBytes;
  }

  /**
   * Check if filename is an image
   */
  private static isImageFile(filename: string): boolean {
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
    const ext = filename.toLowerCase().split('.').pop();
    return imageExtensions.some(validExt => validExt.includes(ext || ''));
  }
}